/**
 * A window of the Organisation Admin Client, on the one Organisation its address
 * names
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 * There is nothing to switch to inside it: another Organisation is another
 * window, opened from the ELASO Instance Admin Client.
 *
 * `organization.open` is the admission answer and what the window shows. For an
 * Instance Manager owner or admin it also records the visit, and refuses when
 * the visit cannot be recorded, so the window shows nothing until it answers
 * and closes if a later answer refuses.
 *
 * Asked again on every socket open, because authority can have been revoked
 * while the socket was down. The answer belongs to one account on one
 * Organisation, so the window is keyed on both and starts over when either
 * changes.
 *
 * An Instance Manager can open a switched-off Organisation, which answers
 * `enabled: false`, to switch it back on. Until then the window offers only its
 * Settings, and mounts no other page, whose procedures would all be refused.
 */
import type {
	AdministrableOrganization,
	OrganizationMe,
} from "@elaso/shared/types/organization";
import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import type { User } from "firebase/auth";
import { Building2, Settings, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import {
	AdminNoAccess,
	type AdminRefusal,
} from "@/components/admin/admin-no-access";
import { type AdminPage, AdminShell } from "@/components/admin/admin-shell";
import { AdminLoading, AdminReadFailed } from "@/components/admin/admin-status";
import { ConnectionWaitScreen } from "@/components/auth/connection-wait";
import {
	AdministeredOrganizationProvider,
	describeAuthority,
} from "@/components/organization/administered-organization";
import {
	SwitchedOffBanner,
	SwitchedOffNotice,
} from "@/components/organization/organization-switched-off";
import { canChangeSettings } from "@/components/organization/settings/organization-settings";
import { useConnectionWait } from "@/hooks/use-connection-wait";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/organization/$organizationId")({
	component: OrganizationWindow,
});

type Opening =
	| { status: "opening" }
	| { status: "open"; organization: AdministrableOrganization }
	| { status: "refused"; refusal: AdminRefusal }
	| { status: "failed"; reason: string };

/**
 * Why the window was refused, as far as the caller may know it. That their own
 * Organisation is switched off is theirs to be told. Anything else stays the
 * one refusal the server gives, which says nothing about an Organisation that
 * is not theirs.
 */
const refusalFor = async (organizationId: string): Promise<AdminRefusal> => {
	const self = await ws
		.sendWithPromise<OrganizationMe>({
			id: ulid(),
			procedure: "organization.me",
			data: {},
		})
		.catch(() => null);
	return self && "switchedOff" in self && self.organizationId === organizationId
		? { kind: "switched-off", organizationName: self.name }
		: { kind: "not-this-organization" };
};

function OrganizationWindow() {
	const { organizationId } = Route.useParams();
	const user = useStore(authStore, (s) => s.user);
	if (!user) return null;
	return (
		<OpenedOrganization
			key={`${user.uid}:${organizationId}`}
			organizationId={organizationId}
			user={user}
		/>
	);
}

const SETTINGS = "/organization/$organizationId/settings";

/**
 * The pages the window offers: Settings only to a caller who can change one,
 * and nothing else while the Organisation is switched off, because every other
 * page's procedures are refused until it is back on.
 */
function pagesFor(
	organization: AdministrableOrganization,
	organizationId: string
): AdminPage[] {
	const params = { organizationId };
	const settings: AdminPage = {
		to: SETTINGS,
		params,
		label: "Settings",
		icon: Settings,
	};
	if (organization.enabled === false) return [settings];
	return [
		{
			to: "/organization/$organizationId",
			params,
			label: "Workspaces",
			icon: Building2,
		},
		{
			to: "/organization/$organizationId/administrators",
			params,
			label: "Administrators",
			icon: ShieldCheck,
		},
		...(canChangeSettings(organization) ? [settings] : []),
	];
}

function OpenedOrganization({
	organizationId,
	user,
}: {
	organizationId: string;
	user: User;
}) {
	const logout = useStore(authStore, (s) => s.logout);
	const { isConnected, connectionEpoch } = useWsConnected();
	const wait = useConnectionWait(isConnected);
	const [opening, setOpening] = useState<Opening>({ status: "opening" });
	const [attempt, setAttempt] = useState(0);
	const matchRoute = useMatchRoute();

	useEffect(() => {
		if (!isConnected) return;
		let current = true;
		// Which other clients the switcher offers. Not part of admission, so not
		// awaited with it.
		coreStore.getState().fetchClientAccess();
		ws.sendWithPromise<AdministrableOrganization>({
			id: ulid(),
			procedure: "organization.open",
			data: { organizationId },
		})
			.then((organization) => {
				if (current) setOpening({ status: "open", organization });
			})
			.catch(async (error: Error & { code?: string }) => {
				if (!current) return;
				if (error.code !== "FORBIDDEN") {
					setOpening({ status: "failed", reason: error.message });
					return;
				}
				const refusal = await refusalFor(organizationId);
				if (current) setOpening({ status: "refused", refusal });
			});
		return () => {
			current = false;
		};
	}, [isConnected, connectionEpoch, organizationId, attempt]);

	switch (opening.status) {
		case "opening":
			// Nothing is asked until the socket opens, so until then the wait is
			// the connection's, and says so.
			return wait.status === "connected" ? (
				<AdminLoading label="Opening organization..." />
			) : (
				<ConnectionWaitScreen wait={wait} />
			);
		case "refused":
			return (
				<AdminNoAccess
					email={user.email}
					onSignOut={logout}
					refusal={opening.refusal}
				/>
			);
		case "failed":
			return (
				<AdminReadFailed
					message={opening.reason}
					onRetry={() => setAttempt((count) => count + 1)}
				/>
			);
		case "open": {
			const { organization } = opening;
			const switchedOff = organization.enabled === false;
			const onSettings = Boolean(
				matchRoute({ to: SETTINGS, params: { organizationId } })
			);
			return (
				<AdminShell
					account={user.email ?? "Unknown account"}
					authority={describeAuthority(organization)}
					client="organisation"
					pages={pagesFor(organization, organizationId)}
					title={
						<h1 className="break-words font-semibold text-lg">
							{organization.name}
						</h1>
					}
				>
					{switchedOff ? <SwitchedOffBanner name={organization.name} /> : null}
					<AdministeredOrganizationProvider
						onReopen={() => setAttempt((count) => count + 1)}
						organization={organization}
					>
						{switchedOff && !onSettings ? (
							<SwitchedOffNotice
								name={organization.name}
								organizationId={organizationId}
							/>
						) : (
							<Outlet />
						)}
					</AdministeredOrganizationProvider>
				</AdminShell>
			);
		}
	}
}
